import { useParams, useNavigate } from 'react-router-dom';
import { useTaskDetail } from '../hooks/useTaskDetail';
import { useTaskSteps } from '../hooks/useTaskSteps';
import { useTaskDiffs } from '../hooks/useTaskDiffs';
import {
  useApprovePlan,
  useRejectPlan,
  usePauseTask,
  useResumeTask,
  useCancelTask,
} from '../hooks/useTaskMutations';
import { TaskStageBadge } from '../components/TaskStageBadge';
import { TaskProgressBar } from '../components/TaskProgressBar';
import { DiagnosticView } from '../components/DiagnosticView';
import { PlanView } from '../components/PlanView';
import { ClarificationForm } from '../components/ClarificationForm';
import { StepsList } from '../components/StepsList';
import { TaskDiffs } from '../components/TaskDiffs';
import { ImpactReport } from '../components/ImpactReport';
import { ApiClientError } from '@shared/api/client';
import { ForbiddenScreen } from '@shared/components/ForbiddenScreen';
import { NotFoundView } from '@shared/components/NotFoundView';
import { useState } from 'react';

export function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { data: task, isLoading, error: taskError } = useTaskDetail(taskId);
  const { data: stepsData } = useTaskSteps(taskId);
  const { data: diffsData } = useTaskDiffs(
    task?.stage === 'completed' || task?.stage === 'executing' || task?.stage === 'monitoring'
      ? taskId
      : undefined
  );

  const approvePlan = useApprovePlan(taskId ?? '');
  const rejectPlan = useRejectPlan(taskId ?? '');
  const pauseTask = usePauseTask(taskId ?? '');
  const resumeTask = useResumeTask(taskId ?? '');
  const cancelTask = useCancelTask(taskId ?? '');

  const [rejectFeedback, setRejectFeedback] = useState('');
  const [showReject, setShowReject] = useState(false);

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">Loading task...</div>;
  }

  if (taskError instanceof ApiClientError && taskError.status === 403) {
    return <ForbiddenScreen message={taskError.message} />;
  }

  if (taskError instanceof ApiClientError && taskError.status === 404) {
    return <NotFoundView message="Task not found." backTo="/tasks" backLabel="Back to Tasks" />;
  }

  if (!task) {
    return <div className="text-center py-8 text-gray-500">Task not found</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/tasks')} className="btn btn-outline text-sm">
          Back
        </button>
        <h2 className="text-lg font-semibold text-gray-900 flex-1 truncate">{task.goal}</h2>
        <TaskStageBadge stage={task.stage} />
      </div>

      {/* Progress bar for executing tasks */}
      {task.stage === 'executing' && task.totalSteps > 0 && (
        <div className="card p-4">
          <TaskProgressBar currentStep={task.currentStep} totalSteps={task.totalSteps} />
        </div>
      )}

      {/* Clarification form */}
      {task.stage === 'clarifying' && task.diagnostic?.questions && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Clarification Needed</h3>
          <ClarificationForm taskId={task.id} questions={task.diagnostic.questions} />
        </div>
      )}

      {/* Diagnostic */}
      {task.diagnostic && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Diagnostic</h3>
          <DiagnosticView diagnostic={task.diagnostic} />
        </div>
      )}

      {/* Plan + approve/reject */}
      {task.plan && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Plan</h3>
          <PlanView plan={task.plan} planVersion={task.planVersion} />

          {task.stage === 'awaiting_approval' && (
            <div className="mt-4 space-y-3 border-t pt-4">
              <div className="flex gap-2">
                <button
                  onClick={() => approvePlan.mutate()}
                  disabled={approvePlan.isPending}
                  className="btn btn-primary text-sm"
                >
                  {approvePlan.isPending ? 'Approving...' : 'Approve Plan'}
                </button>
                <button
                  onClick={() => setShowReject(!showReject)}
                  className="btn btn-outline text-sm"
                >
                  Reject
                </button>
              </div>
              {showReject && (
                <div className="space-y-2">
                  <textarea
                    value={rejectFeedback}
                    onChange={(e) => setRejectFeedback(e.target.value)}
                    placeholder="What should be changed?"
                    className="input"
                    rows={2}
                  />
                  <button
                    onClick={() => rejectPlan.mutate(rejectFeedback)}
                    disabled={rejectPlan.isPending || !rejectFeedback}
                    className="btn bg-red-500 text-white hover:bg-red-600 text-sm"
                  >
                    Submit Rejection
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Steps list */}
      {stepsData?.steps && stepsData.steps.length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Steps</h3>
          <StepsList steps={stepsData.steps} taskId={task.id} />
        </div>
      )}

      {/* Diffs */}
      {diffsData?.diffs && diffsData.diffs.length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Changes</h3>
          <TaskDiffs diffs={diffsData.diffs} />
        </div>
      )}

      {/* Results summary */}
      {task.resultsSummary && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Results</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center text-sm">
            <div className="p-3 bg-green-50 rounded-lg">
              <div className="text-lg font-bold text-green-700">
                {task.resultsSummary.stepsCompleted}
              </div>
              <div className="text-xs text-gray-500">Completed</div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <div className="text-lg font-bold text-red-700">
                {task.resultsSummary.stepsFailed}
              </div>
              <div className="text-xs text-gray-500">Failed</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-lg font-bold text-gray-700">
                {task.resultsSummary.productsModified}
              </div>
              <div className="text-xs text-gray-500">Products Modified</div>
            </div>
            <div className="p-3 bg-indigo-50 rounded-lg">
              <div className="text-lg font-bold text-indigo-700">
                +{task.resultsSummary.avgScoreImprovement.toFixed(1)}
              </div>
              <div className="text-xs text-gray-500">Avg Score</div>
            </div>
          </div>
        </div>
      )}

      {/* Monitoring info */}
      {task.stage === 'monitoring' && (
        <div className="card p-4 border-teal-200 bg-teal-50">
          <p className="text-sm text-teal-800">
            Re-audit scheduled. The impact report will be available after the scheduled re-analysis
            completes.
          </p>
        </div>
      )}

      {/* Impact report for completed and monitoring tasks */}
      {(task.stage === 'completed' || task.stage === 'monitoring') && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Impact Report</h3>
          <ImpactReport taskId={task.id} />
        </div>
      )}

      {/* Error message */}
      {task.errorMessage && (
        <div className="card p-4 border-red-200 bg-red-50">
          <p className="text-sm text-red-700">{task.errorMessage}</p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        {task.stage === 'executing' && (
          <button
            onClick={() => pauseTask.mutate()}
            disabled={pauseTask.isPending}
            className="btn btn-outline text-sm"
          >
            Pause
          </button>
        )}
        {task.stage === 'paused' && (
          <button
            onClick={() => resumeTask.mutate()}
            disabled={resumeTask.isPending}
            className="btn btn-primary text-sm"
          >
            Resume
          </button>
        )}
        {!['completed', 'failed', 'monitoring'].includes(task.stage) && (
          <button
            onClick={() => cancelTask.mutate()}
            disabled={cancelTask.isPending}
            className="btn bg-red-500 text-white hover:bg-red-600 text-sm"
          >
            Cancel Task
          </button>
        )}
      </div>
    </div>
  );
}
